import { useState } from "react";
import useAddCollectionQrStats from "../../../components/hooks/useAddCollectionQrStats";
import DSCard from "views/system-design/DSCard";
import DSCardContent from "views/system-design/DSCardContent";
import DSBox from "views/system-design/DSBox";
import DSStack from "views/system-design/DSStack";
import DSTypography from "views/system-design/DSTypography";
import DSSelect from "views/system-design/DSSelect";
import DSMenuItem from "views/system-design/DSMenuItem";
import DSSecondaryButton from "views/system-design/DSSecondaryButton";
import DSCircularProgress from "views/system-design/DSCircularProgress";

/**
 * Add Collection QR performance.
 *
 * The window applies to SCANS only: "Paying clocks" counts how many of the clocks that
 * scanned in the window have ever paid for a collection, at any time — so it reads as
 * "scanners who are also customers", not as purchases the QR caused.
 */
function AddCollectionQrCard() {
  const [days, setDays] = useState<number>(30);
  const [enabled, setEnabled] = useState(false);
  const { data, isLoading, refetch } = useAddCollectionQrStats(days, enabled);

  const handleLoad = () => {
    if (enabled) {
      refetch();
    } else {
      setEnabled(true);
    }
  };

  return (
    <DSCard sx={{ height: "100%" }}>
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          pt: 2,
          gap: 1,
        }}
      >
        <DSTypography variant="h6">Add Collection QR</DSTypography>
        <DSStack direction="row" alignItems="center" spacing={1}>
          <DSTypography variant="caption" sx={{ color: "text.secondary" }}>
            Last
          </DSTypography>
          <DSSelect<number>
            size="small"
            value={days}
            onChange={(e) => {
              setDays(Number(e.target.value));
              setEnabled(false);
            }}
            sx={{ fontSize: 12 }}
          >
            {[7, 14, 30, 60, 90].map((d) => (
              <DSMenuItem key={d} value={d}>
                {d}d
              </DSMenuItem>
            ))}
          </DSSelect>
          <DSSecondaryButton
            size="small"
            onClick={handleLoad}
            disabled={isLoading}
          >
            {isLoading ? (
              <DSCircularProgress size={20} />
            ) : data ? (
              "Refresh"
            ) : (
              "Load"
            )}
          </DSSecondaryButton>
        </DSStack>
      </DSBox>
      <DSCardContent>
        {isLoading ? (
          <DSBox sx={{ textAlign: "center", p: "20px" }}>
            <DSCircularProgress />
          </DSBox>
        ) : data ? (
          <DSBox
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              textAlign: "center",
              gap: 1,
            }}
          >
            <DSBox>
              <DSTypography sx={{ fontSize: 28, fontWeight: "bold" }}>
                {data.scans.toLocaleString()}
              </DSTypography>
              <DSTypography sx={{ fontSize: 12, color: "text.secondary" }}>
                Scans
              </DSTypography>
            </DSBox>
            <DSBox>
              <DSTypography sx={{ fontSize: 28, fontWeight: "bold" }}>
                {data.uniqueClocks.toLocaleString()}
              </DSTypography>
              <DSTypography sx={{ fontSize: 12, color: "text.secondary" }}>
                Scanned clocks
              </DSTypography>
            </DSBox>
            <DSBox>
              <DSTypography sx={{ fontSize: 28, fontWeight: "bold" }}>
                {data.clocksThatPaid.toLocaleString()}
              </DSTypography>
              <DSTypography sx={{ fontSize: 12, color: "text.secondary" }}>
                Paying scanners
                {data.uniqueClocks > 0 &&
                  ` (${Math.round(
                    (data.clocksThatPaid / data.uniqueClocks) * 100,
                  )}%)`}
              </DSTypography>
            </DSBox>
          </DSBox>
        ) : (
          <DSBox
            sx={{ textAlign: "center", p: "20px", color: "text.secondary" }}
          >
            Click Load to fetch data
          </DSBox>
        )}
      </DSCardContent>
    </DSCard>
  );
}

export default AddCollectionQrCard;
